import React, { useEffect } from 'react';

const styles = {
  page: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '40px 24px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: '32px',
  },
  content: {
    fontSize: '16px',
    lineHeight: 1.8,
    color: 'var(--text)',
    background: 'var(--surface)',
    padding: '40px',
    borderRadius: '16px',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow)',
  },
  h3: {
    marginTop: '32px',
    marginBottom: '16px',
    color: 'var(--text)',
    fontSize: '20px',
    fontWeight: 600,
  },
  p: {
    marginBottom: '20px',
  }
};

const LEGAL_CONTENT = {
  privacy: {
    title: "Privacy Policy",
    description: "Learn how Convert All Docs handles your data and protects your privacy.",
    content: (
      <>
        <p style={styles.p}>Last updated: April 2026</p>
        <p style={styles.p}>This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service.</p>
        <h3 style={styles.h3}>File Processing & Privacy</h3>
        <p style={styles.p}>Convert All Docs converts your files directly in memory or via temporary storage that is immediately securely deleted after your conversion is complete. We do not permanently store, view, or claim ownership of any files you upload.</p>
        <h3 style={styles.h3}>Google AdSense & Cookies</h3>
        <p style={styles.p}>We use Google AdSense to display ads. Third party vendors, including Google, use cookies to serve ads based on a user's prior visits to this website or other websites.</p>
        <p style={styles.p}>Google's use of advertising cookies enables it and its partners to serve ads to our users based on their visit to our sites and/or other sites on the Internet.</p>
        <p style={styles.p}>Users may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer" style={{color: 'var(--accent)'}}>Ads Settings</a>.</p>
        <h3 style={styles.h3}>Data Analytics</h3>
        <p style={styles.p}>We may use standard web analytics tools to see how users interact with our tool to improve our services. This data is fully anonymized.</p>
      </>
    )
  },
  terms: {
    title: "Terms of Service",
    description: "Read the rules and guidelines for using Convert All Docs's free file conversion tools.",
    content: (
      <>
        <p style={styles.p}>By using Convert All Docs, you agree to these Terms of Service. Please read them carefully.</p>
        <h3 style={styles.h3}>Usage License</h3>
        <p style={styles.p}>You are permitted to use Convert All Docs for personal and commercial file conversions. You may not use this tool to convert illegal or malicious files, nor attempt to reverse-engineer the backend architecture.</p>
        <h3 style={styles.h3}>Limitation of Liability</h3>
        <p style={styles.p}>Convert All Docs is provided "as is" without warranty of any kind. We are not liable for any corrupted files, data loss, or damages resulting from the use of this free tool. Always keep backups of your original files.</p>
      </>
    )
  },
  about: {
    title: "About Convert All Docs",
    description: "Learn more about Convert All Docs, the fastest and most secure online file converter.",
    content: (
      <>
        <p style={styles.p}>Convert All Docs is a modern, privacy-first file conversion utility designed to make format shifting completely seamless.</p>
        <p style={styles.p}>Built for speed and simplicity, Convert All Docs supports dozens of formats across Documents, Images, PDFs, and Data files. Whether you are merging a PDF, compressing an image into AVIF, or extracting tables from Excel, Convert All Docs handles the heavy lifting instantly.</p>
        <p style={styles.p}>Our mission is to provide an enterprise-grade conversion suite that doesn't sacrifice your privacy. We process files dynamically and never hoard your personal data.</p>
      </>
    )
  },
  contact: {
    title: "Contact Us",
    description: "Get in touch with the Convert All Docs team for support, feature requests, or inquiries.",
    content: (
      <>
        <p style={styles.p}>Have a question, feature request, or found a bug? We'd love to hear from you.</p>
        <p style={styles.p}>You can reach our development team via email at:</p>
        <p style={{...styles.p, fontWeight: 'bold', color: 'var(--accent)'}}>support@convertalldocs.com</p>
        <p style={styles.p}>Or open an issue on our GitHub repository linked in the footer!</p>
      </>
    )
  }
};

export default function LegalPage({ type }) {
  const data = LEGAL_CONTENT[type];

  // Update SEO Meta Tags & Canonical URL
  useEffect(() => {
    const baseUrl = 'https://convertalldocs.com'; // Change to your actual domain
    const canonicalUrl = `${baseUrl}/${type}`;

    // Update Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // Update Open Graph Tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = canonicalUrl;

    if (data) {
      const title = `${data.title} | Convert All Docs`;
      document.title = title;
      ogTitle.content = title;
      
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = data.description;
    }
  }, [data, type]);

  if (!data) return <div style={styles.page}>Page not found.</div>;

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>{data.title}</h1>
      <div style={styles.content}>
        {data.content}
      </div>
    </div>
  );
}
